import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { loadStripe } from '@stripe/stripe-js'; // Import loadStripe
import StepIndicator from './StepIndicator'; // Import shared component
import './Step3Component.css';

// Load Stripe outside of the component render cycle
// Use your publishable key via environment variable
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Package Details Component (based on image)
const PackageDetails = ({ styleName }) => (
  <div className="package-details-card">
    <div className="details-left">
      <h4 className="package-title">Your Fably Package Includes:</h4>
      <ul>
        <li>
          <span className="icon">✨</span> {/* Sparkles icon */}
          1 Animation in <span className="style-highlight">{styleName}</span> style
        </li>
        <li>
          <span className="icon">⏱️</span> {/* Clock icon */}
          Delivered within 45-55 minutes
        </li>
        <li>
          <span className="icon">✉️</span> {/* Envelope icon */}
          Sent directly to your email
        </li>
      </ul>
    </div>
    <div className="details-right">
      <div className="price-box">
        <span className="price">$8.99</span>
        <span className="price-label">total price</span>
      </div>
    </div>
  </div>
);

PackageDetails.propTypes = {
  styleName: PropTypes.string.isRequired,
};


const Step3Component = ({ selectedStyle, inputText, userEmail: initialEmail, onEmailChange, onSubmit: originalOnSubmit, onBack, currentStep, completedSteps }) => {
  const [email, setEmail] = useState(initialEmail || '');
  const [isValid, setIsValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setIsValid(emailRegex.test(email));
    // Pass email up whenever it changes and is valid
    if (emailRegex.test(email)) {
      onEmailChange(email);
    } else {
       onEmailChange(''); // Clear if invalid
    }
  }, [email, onEmailChange]);

  const handleEmailInputChange = (e) => {
    setEmail(e.target.value);
    setError(null); // Clear error on input change
  };

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return;

    setIsSubmitting(true);
    setError(null);

    // --- Determine Style Name --- 
    let styleNameToSend = '';
    if (!selectedStyle) {
      setError('Animation style is missing. Please go back and select a style.');
      setIsSubmitting(false);
      return;
    } else if (selectedStyle.id === 'dreamy') {
      styleNameToSend = 'dreamy';
    } else if (selectedStyle.id === 'no_lineart') {
      styleNameToSend = 'fably';
    } else {
      // Optional: Handle unexpected style IDs if necessary
      console.warn('Unknown style ID selected:', selectedStyle.id);
      setError('An unexpected error occurred with the selected style. Please try again.');
      setIsSubmitting(false);
      return;
    }
    // --- End Determine Style Name ---

    const bookName = inputText; 
    if (!bookName) {
        setError('Book title/text input is missing.');
        setIsSubmitting(false);
        return;
    }

    try {
      // 1. Call your backend using environment variable for base URL
      const backendUrl = `${process.env.REACT_APP_API_URL}/api/ask-generation`;
      const response = await fetch(backendUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookName: bookName,
          userEmail: email,
          styleName: styleNameToSend // Add the determined style name
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // 2. Expect sessionId from backend
      if (data.sessionId) {
        console.log('Received Stripe Session ID:', data.sessionId);
        
        // 3. Redirect to Stripe Checkout
        const stripe = await stripePromise;
        const { error: stripeError } = await stripe.redirectToCheckout({
          sessionId: data.sessionId,
        });
        
        // This point is only reached if redirectToCheckout fails (e.g., network error)
        if (stripeError) {
          console.error("Stripe redirectToCheckout error:", stripeError);
          setError(stripeError.message || 'Failed to redirect to payment.');
          setIsSubmitting(false); // Stop loading if redirect failed
        }
        // If successful, the user is redirected and won't see subsequent frontend code execution here.

      } else {
        console.error('Backend did not return sessionId.');
        setError('Could not initiate payment process. Please try again.');
        setIsSubmitting(false);
      }

    } catch (err) {
      setError(err.message || "An error occurred. Please try again.");
      setIsSubmitting(false);
    }
    // Note: setIsSubmitting(false) is handled within error paths now,
    // because successful redirect means the component might unmount.
  };

  return (
    <div> 
      {/* Wrap Indicator and Title */}
      <div className="card__header">
        <h2 className="card__title">Almost Done!</h2> 
        <StepIndicator currentStep={currentStep} completedSteps={completedSteps} /> 
      </div>

      {/* Note: Removed card__content class from this paragraph */}
      <p className="step3-subtitle">Enter email to receive your art</p> 

      {/* Note: Added card__content class here to wrap form + package */}
      <div className="card__content"> 
        <div className="card__form">
          <div className="email-input-wrapper"> 
            <input 
              type="email"
              className={`email-input ${email && !isValid ? 'invalid' : ''}`}
              placeholder="you@example.com"
              value={email}
              onChange={handleEmailInputChange}
              aria-label="Email Address"
              disabled={isSubmitting}
            />
             {email && !isValid && (
              <p className="validation-msg">Please enter a valid email</p>
            )}
          </div>
          
          {error && <p className="error-message">{error}</p>} 

          <PackageDetails styleName={selectedStyle?.name || 'Selected'} />

          {/* --- Add Call Founder Section --- */}
          <div className="call-founder-container">
            <a href="https://fably.daily.co/callmenow" target="_blank" rel="noopener noreferrer" className="call-founder-link">
              <button className="button" aria-label="Call the founder" type="button"> 
                <svg xmlns="http://www.w3.org/2000/svg" width="32" viewBox="0 0 32 32" height="32" fill="none" className="svg-icon">
                  <path strokeWidth="2" strokeLinecap="round" stroke="#fff" fillRule="evenodd" d="m24.8868 19.1288c-1.0274-.1308-2.036-.3815-3.0052-.7467-.7878-.29-1.6724-.1034-2.276.48-.797.8075-2.0493.9936-2.9664.3258-1.4484-1.055-2.7233-2.3295-3.7783-3.7776-.6681-.9168-.4819-2.1691.3255-2.9659.5728-.6019.7584-1.4748.4802-2.2577-.3987-.98875-.6792-2.02109-.8358-3.07557-.2043-1.03534-1.1138-1.7807-2.1694-1.77778h-3.18289c-.60654-.00074-1.18614.25037-1.60035.69334-.40152.44503-.59539 1.03943-.53345 1.63555.344 3.31056 1.47164 6.49166 3.28961 9.27986 1.64878 2.5904 3.84608 4.7872 6.43688 6.4356 2.7927 1.797 5.9636 2.9227 9.2644 3.289h.1778c.5409.0036 1.0626-.2 1.4581-.569.444-.406.6957-.9806.6935-1.5822v-3.1821c.0429-1.0763-.7171-2.0185-1.7782-2.2046z" clipRule="evenodd"></path>
                </svg>
              </button>
              <p className="call-founder-text">On the fence? Talk to the founder Now!</p>
            </a>
          </div>
          {/* --- End Call Founder Section --- */}

        </div>
      </div>

      <div className="nav-area dual-button"> 
        <button className="card__button secondary back-button" onClick={onBack} disabled={isSubmitting}> 
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="button-arrow arrow-left">
              <path d="M7.828 11H20v2H7.828l5.364 5.364-1.414 1.414L4 12l7.778-7.778 1.414 1.414z"/>
          </svg>
          Back
        </button>

        <button 
            className={`card__button submit-button ${!isValid || isSubmitting ? 'disabled' : ''}`}
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
          {isSubmitting ? 'Processing...' : 'ANIMATE NOW !'}
        </button>
      </div>
    </div> 
  );
};

Step3Component.propTypes = {
  selectedStyle: PropTypes.object,
  inputText: PropTypes.string,
  userEmail: PropTypes.string,
  onEmailChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  currentStep: PropTypes.number.isRequired,
  completedSteps: PropTypes.number.isRequired,
};

export default Step3Component; 